import first from "lodash/first";
import { useRouter } from "next/router";
import type { FunctionComponentWithRef, MouseEvent } from "react";
import { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { checkIsPlusArticle } from "scmp-app/components/article/article-render/plus/helpers";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { getContentSponsor } from "scmp-app/components/content/content-sponsor-tag/helpers";
import { usePlusArticlePopup } from "scmp-app/components/plus/article-popup/hooks";
import { DefaultPageSize } from "scmp-app/components/search/consts";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { SearchEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { useOptimizelyContext } from "scmp-app/lib/optimizely/context";
import { pathname as plusSearchPathname } from "scmp-app/pages/plus/search";
import type { searchContentItemContent$key } from "scmp-app/queries/__generated__/searchContentItemContent.graphql";

import {
  Authors,
  Container,
  Cover,
  Footer,
  Header,
  LiveTagContainer,
  PrintHeadline,
  StyledEntityLink,
  StyledImagePlusFlag,
  Summary,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: searchContentItemContent$key;
};

export const ContentItemSearch: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  ref: forwardReference,
  reference: reference_,
}) => {
  const content = useFragment(
    graphql`
      fragment searchContentItemContent on Content {
        __typename
        entityId
        authors {
          entityId
        }
        topics {
          entityId
        }
        sectionsV2 {
          value {
            ... on Section {
              entityId
            }
            ... on VideoSection {
              entityId
            }
          }
        }
        ...entityLink
        ...helpersUseContentSponsorContent
        ...hooksContentItemProvidersContent
          @arguments(
            withLiveTag: true
            withHeadline: true
            withCoverImage: true
            withCoverImageSize540x360: true
            withSummary: true
            withActionBar: true
            withPrintHeadline: true
            withSponsorTag: true
            withAuthorImage: true
            withAuthorLink: true
            withVideoDurationOverlay: true
            withGalleryOverlay: true
          )
        ...hooksPlusArticleLinkHandlerArticle
        ...helpersCheckCanShowInPlusPlatformContent
        ...helpersCheckIsPlusArticle
      }
    `,
    reference_,
  );

  const providers = useContentItemProviders(content);
  const { route } = useRouter();

  const isOnPlusSearch = useMemo(() => route === plusSearchPathname, [route]);

  const needToShowPlusFlag = useMemo(() => checkIsPlusArticle(content), [content]);

  const header = useMemo(
    () => (
      <>
        {needToShowPlusFlag && <StyledImagePlusFlag />}
        <LiveTagContainer>{providers.liveTag()}</LiveTagContainer>
        {providers.headline({
          hideTooltip: true,
          useSectionTags: true,
        })}
      </>
    ),
    [needToShowPlusFlag, providers],
  );

  const cover = providers.coverImage({
    responsiveVariants: {
      desktopUp: "size540x360",
      mobileUp: "size540x360",
      tabletUp: "size540x360",
    },
  });

  const contentSponsor = getContentSponsor(content);
  const isSponsorArticle = notEmpty(contentSponsor?.name);
  const summary = isSponsorArticle ? null : providers.summary();

  const { articleResultIdMap, articleTypeText, currentValue, dateRangeText, orderBy, sectionIds } =
    useSearchContext();
  const { fireEvent } = useOptimizelyContext();

  const authors = useMemo(
    () => (
      <>
        {providers.authorImage({
          ignoreAuthorType: true,
        })}
        {providers.author({
          ignoreAuthorType: true,
          isMultiple: true,
          onClick: (_entityUuid: string, entityId?: string) => {
            const resultId = articleResultIdMap?.get(content.entityId)?.resultId ?? "";
            // GA4 21.2.1
            sendGA4Tracking({
              action: "click",
              category: "search",
              customized_parameters: {
                author_id: entityId,
                is_plus_article: needToShowPlusFlag ? "yes" : "no",
                search_id: resultId,
                search_text: currentValue,
              },
              subcategory: "result",
            });
          },
        })}
      </>
    ),
    [articleResultIdMap, content.entityId, currentValue, needToShowPlusFlag, providers],
  );

  const listOfSectionIds =
    first(content.sectionsV2)
      ?.value?.map(section => section?.entityId)
      .join(",") ?? "";

  const listOfTopicIds = content.topics?.map(topic => topic?.entityId).join(",") ?? "";

  const { handleOpen } = usePlusArticlePopup();

  const handleItemClick = useCallback(
    (event: MouseEvent) => {
      const page = articleResultIdMap?.get(content.entityId)?.page;
      const position = articleResultIdMap?.get(content.entityId)?.position;
      const resultId = articleResultIdMap?.get(content.entityId)?.resultId;

      const getOrderBy = () => {
        if (orderBy?.field === "PUBLISHED_DATE") {
          switch (orderBy.direction) {
            case "ASC":
              return "oldest";
            case "DESC":
              return "newest";
          }
        }
        return "relevance";
      };

      const getSection = () => {
        if (!notEmpty(sectionIds)) return "All";
        return sectionIds.join(",");
      };

      // GA4 21.2.1
      sendGA4Tracking({
        action: "click",
        category: "search",
        customized_parameters: {
          article_id: content?.entityId,
          article_type: articleTypeText,
          author_id: first(content?.authors)?.entityId,
          date_range: dateRangeText,
          is_plus_article: needToShowPlusFlag ? "yes" : "no",
          page,
          position,
          search_id: resultId,
          search_text: currentValue,
          section: getSection(),
          section_id: listOfSectionIds,
          sort: getOrderBy(),
          topic_id: listOfTopicIds,
        },
        subcategory: "result",
      } as SearchEvent);

      fireEvent?.({
        eventName: "item_click",
        tags: {
          value:
            page !== undefined && position !== undefined
              ? Math.floor(0.8 ^ ((page - 1) * DefaultPageSize + position - 1))
              : 0,
        },
      });

      if (isOnPlusSearch) {
        event.preventDefault();
        handleOpen(content);
      }
    },
    [
      articleResultIdMap,
      articleTypeText,
      content,
      currentValue,
      dateRangeText,
      fireEvent,
      handleOpen,
      isOnPlusSearch,
      listOfSectionIds,
      listOfTopicIds,
      needToShowPlusFlag,
      orderBy,
      sectionIds,
    ],
  );

  const footer = isSponsorArticle
    ? providers.sponsorTag?.({
        color: "#001246",
        gap: "4px",
        layoutDirection: "horizontal",
        nameFontSize: "12px",
        typeFontSize: "12px",
      })
    : providers.actionBar({
        displayDateVariant: "publishedDate",
        isBookmarkVisible: false,
        isCommentsVisible: false,
      });

  const renderContent = useCallback(() => {
    const resultContent = () => {
      switch (content.__typename) {
        case "Gallery":
        case "Video":
          return (
            <>
              <Header>{header}</Header>
              <Cover>
                {cover}
                {content.__typename === "Video" &&
                  providers.video?.durationOverlay({
                    iconSize: "small",
                    variant: "homepage",
                  })}
                {content.__typename === "Gallery" &&
                  providers.gallery?.indicator({ withValue: false })}
              </Cover>
              <Authors />
              <Summary />
              <PrintHeadline />
              <Footer>{footer}</Footer>
            </>
          );
        default:
          return (
            <>
              <Header>{header}</Header>
              <Cover>{cover}</Cover>
              <Authors>{authors}</Authors>
              <Summary>{summary}</Summary>
              <Footer>{footer}</Footer>
              <PrintHeadline>{providers.printHeadline()}</PrintHeadline>
            </>
          );
      }
    };

    return (
      <StyledEntityLink
        onClick={handleItemClick}
        reference={content}
        target={isOnPlusSearch ? "_blank" : "_self"}
      >
        {resultContent()}
      </StyledEntityLink>
    );
  }, [
    authors,
    content,
    cover,
    footer,
    handleItemClick,
    header,
    isOnPlusSearch,
    providers,
    summary,
  ]);

  return (
    <Container className={className} ref={forwardReference}>
      {renderContent()}
    </Container>
  );
};

ContentItemSearch.displayName = "ContentItemSearch";
